'use client'

import Image from "next/image";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import CesiumProjectPopup from "./CesiumProjectPopup";
import { getLocalizedProjectUrl } from "@/utils/projectUtils";
import {
    CLUSTER_PIXEL_RANGE,
    INITIAL_RANGE_METERS,
    MINIMUM_CLUSTER_SIZE,
    TILESET_SHOW_MULTIPLIER,
    VIETNAM_CENTER,
    buildClusterBillboardImage,
    getProjectTitle,
    rebuildProjectEntities,
} from "./cesium-marker-helpers";
import type { GlobeProject } from "./google-maps-globe-clustering";

type CesiumCartesian3 = { x: number; y: number; z: number };
type CesiumCartographic = { longitude: number; latitude: number; height: number };
type CesiumBoundingSphere = { center: CesiumCartesian3; radius: number };
type CesiumTileset = { show: boolean; boundingSphere: CesiumBoundingSphere };
type ProvinceMode = "34" | "63";

type CesiumEntity = {
    id: string;
    properties?: { project?: { getValue: () => GlobeProject } };
};

type CesiumClusterStyleTarget = {
    billboard: { show: boolean; image: string; verticalOrigin: number; disableDepthTestDistance: number };
    label: { show: boolean };
    point: { show: boolean };
};

type CesiumPickResult = {
    id?: CesiumEntity;
    primitive?: { position?: CesiumCartesian3 };
};

type CesiumDataSourceClustering = {
    enabled: boolean;
    pixelRange: number;
    minimumClusterSize: number;
    clusterEvent: { addEventListener: (cb: (entities: CesiumEntity[], cluster: CesiumClusterStyleTarget) => void) => void };
};

type CesiumDataSource = {
    name?: string;
    entities: { add: (options: Record<string, unknown>) => CesiumEntity; removeAll: () => void };
    clustering: CesiumDataSourceClustering;
};

type CesiumScreenSpaceClickEvent = { position: { x: number; y: number } };
type CesiumScreenSpaceMoveEvent = { endPosition: { x: number; y: number } };

type CesiumScreenSpaceEventHandler = {
    setInputAction: {
        (cb: (event: CesiumScreenSpaceClickEvent) => void, type: number): void;
        (cb: (event: CesiumScreenSpaceMoveEvent) => void, type: number): void;
    };
    destroy: () => void;
};

type CesiumViewer = {
    scene: {
        globe: { depthTestAgainstTerrain: boolean };
        primitives: { add: (primitive: unknown) => unknown };
        canvas: HTMLCanvasElement;
        pick: (position: { x: number; y: number }) => CesiumPickResult | undefined;
    };
    camera: {
        position: CesiumCartesian3;
        positionCartographic: { height: number };
        setView: (options: { destination: CesiumCartesian3 }) => void;
        flyTo: (options: { destination: CesiumCartesian3; duration?: number }) => void;
        moveEnd: { addEventListener: (cb: () => void) => () => void };
        moveStart: { addEventListener: (cb: () => void) => () => void };
    };
    dataSources: { add: (ds: CesiumDataSource) => void; remove: (ds: CesiumDataSource, destroy?: boolean) => void };
    terrainProvider: unknown;
    isDestroyed: () => boolean;
    destroy: () => void;
};

type CesiumRuntime = {
    Ion: { defaultAccessToken: string };
    Viewer: new (container: Element, options: Record<string, unknown>) => CesiumViewer;
    CesiumTerrainProvider: { fromIonAssetId: (assetId: number) => Promise<unknown> };
    Cesium3DTileset: { fromIonAssetId: (assetId: number) => Promise<CesiumTileset> };
    ImageryLayer: any;
    UrlTemplateImageryProvider: any;
    GeoJsonDataSource: {
        load: (data: string | Record<string, unknown>, options?: Record<string, unknown>) => Promise<CesiumDataSource & {
            entities: CesiumDataSource["entities"] & { values: Array<Record<string, any>> };
        }>;
    };
    Cartesian3: {
        fromDegrees: (lng: number, lat: number, height?: number) => CesiumCartesian3;
        distance: (a: CesiumCartesian3, b: CesiumCartesian3) => number;
    };
    Cartographic: {
        fromCartesian: (cartesian: CesiumCartesian3) => CesiumCartographic;
    };
    Math: {
        toDegrees: (radians: number) => number;
    };
    CustomDataSource: new (name: string) => CesiumDataSource;
    VerticalOrigin: { BOTTOM: number; CENTER: number; TOP: number };
    HorizontalOrigin: { CENTER: number };
    HeightReference: { RELATIVE_TO_GROUND: number };
    ClassificationType: { TERRAIN: number };
    LabelStyle: { FILL_AND_OUTLINE: number };
    Color: {
        WHITE: unknown;
        BLACK: unknown;
        fromCssColorString: (color: string) => { withAlpha: (alpha: number) => unknown };
    };
    Cartesian2: new (x: number, y: number) => unknown;
    ScreenSpaceEventHandler: new (canvas: HTMLCanvasElement) => CesiumScreenSpaceEventHandler;
    ScreenSpaceEventType: { LEFT_CLICK: number; MOUSE_MOVE: number };
};

const CESIUM_VERSION = "1.121";
const CESIUM_BASE_URL = `https://cesium.com/downloads/cesiumjs/releases/${CESIUM_VERSION}/Build/Cesium`;
const CESIUM_TOKEN = process.env.NEXT_PUBLIC_CESIUM_ION_TOKEN || "";
const CESIUM_ASSET_ID = 4502814;
const SG3D_LOGO_SRC = "/images/H_Logo_Light.png";
const PROVINCE_GEOJSON_URLS: Record<ProvinceMode, string> = {
    "34": "/geojson/vietnam.geojson",
    "63": "/geojson/vietnamold.geojson",
};
const PROVINCE_LINE_COLORS: Record<ProvinceMode, string> = {
    "34": "#FFD400",
    "63": "#FFFFFF",
};
const PROVINCE_FILL_COLOR = "#00A3FF";
const provinceGeoJsonCache: Partial<Record<ProvinceMode, Promise<any>>> = {};

const getProvinceLineColor = (provinceMode: ProvinceMode) => PROVINCE_LINE_COLORS[provinceMode];

const getCesiumRuntime = () => (window as Window & { Cesium?: CesiumRuntime }).Cesium;

const loadProvinceGeoJson = (provinceMode: ProvinceMode) => {
    if (!provinceGeoJsonCache[provinceMode]) {
        const geojsonUrl = PROVINCE_GEOJSON_URLS[provinceMode];
        provinceGeoJsonCache[provinceMode] = fetch(geojsonUrl)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch ${geojsonUrl}`);
                }

                return response.json();
            })
            .catch((error) => {
                delete provinceGeoJsonCache[provinceMode];
                throw error;
            });
    }

    return provinceGeoJsonCache[provinceMode]!;
};

const resolveProvinceGeoJson = (provinceMode: ProvinceMode, geojson?: any) => {
    if (geojson) {
        provinceGeoJsonCache[provinceMode] = Promise.resolve(geojson);
    }

    return loadProvinceGeoJson(provinceMode);
};

const loadCesiumAssets = () => new Promise<void>((resolve, reject) => {
    if (getCesiumRuntime()) {
        resolve();
        return;
    }

    (window as Window & { CESIUM_BASE_URL?: string }).CESIUM_BASE_URL = CESIUM_BASE_URL;

    if (!document.getElementById("cesium-widgets-css")) {
        const link = document.createElement("link");
        link.id = "cesium-widgets-css";
        link.rel = "stylesheet";
        link.href = `${CESIUM_BASE_URL}/Widgets/widgets.css`;
        document.head.appendChild(link);
    }

    const existingScript = document.getElementById("cesium-script") as HTMLScriptElement | null;
    if (existingScript) {
        if (getCesiumRuntime() || existingScript.dataset.loaded === "true") {
            resolve();
            return;
        }
        existingScript.addEventListener("load", () => resolve(), { once: true });
        existingScript.addEventListener("error", () => reject(new Error("Cesium load failed")), { once: true });
        return;
    }

    const script = document.createElement("script");
    script.id = "cesium-script";
    script.src = `${CESIUM_BASE_URL}/Cesium.js`;
    script.async = true;
    script.onload = () => {
        script.dataset.loaded = "true";
        resolve();
    };
    script.onerror = () => reject(new Error("Cesium load failed"));
    document.head.appendChild(script);
});

type CesiumGlobeViewProps = {
    locale: string;
    projects: GlobeProject[];
    provinceMode: ProvinceMode;
    provinceGeoJson?: any;
    selectedCountry: string;
};

export type LatLngBounds = {
    minLat: number;
    maxLat: number;
    minLng: number;
    maxLng: number;
};

export type CesiumGlobeViewHandle = {
    flyToCoords: (lat: number, lng: number, height?: number) => void;
    fitBoundsLatLng: (bounds: LatLngBounds) => void;
    resetView: () => void;
};

const SINGLE_PROJECT_HEIGHT_METERS = 2500;
const METERS_PER_DEGREE = 111_000;
const FIT_BOUNDS_PADDING_FACTOR = 1.6;
const MIN_FIT_BOUNDS_HEIGHT_METERS = 5_000;
const MAX_FIT_BOUNDS_HEIGHT_METERS = 8_000_000;

const isTouchDevice = () => typeof window !== "undefined" && window.matchMedia("(hover: none)").matches;

const drawVietnamProvincePolylines = (Cesium: CesiumRuntime, geojson: any, lineColor: string) => {
    const lineDataSource = new Cesium.CustomDataSource("Đường ranh giới tỉnh thành Việt Nam");
    const lineMaterial = Cesium.Color.fromCssColorString(lineColor).withAlpha(0.95);

    const addRing = (ring: number[][], provinceName?: string) => {
        if (!Array.isArray(ring) || ring.length < 2) return;

        const positions = ring
            .filter((coord) => Number.isFinite(coord?.[0]) && Number.isFinite(coord?.[1]))
            .map(([longitude, latitude]) => Cesium.Cartesian3.fromDegrees(longitude, latitude));

        if (positions.length < 2) return;

        lineDataSource.entities.add({
            name: provinceName || "Ranh giới tỉnh thành",
            polyline: {
                positions,
                clampToGround: true,
                width: 2.5,
                material: lineMaterial,
            },
        });
    };

    geojson?.features?.forEach((feature: any) => {
        const provinceName = feature.properties?.ten_tinh || feature.properties?.shapeName;
        const geometry = feature.geometry;
        if (geometry?.type === "Polygon") {
            geometry.coordinates.forEach((ring: number[][]) => addRing(ring, provinceName));
        }
        if (geometry?.type === "MultiPolygon") {
            geometry.coordinates.forEach((polygon: number[][][]) => {
                polygon.forEach((ring) => addRing(ring, provinceName));
            });
        }
    });

    return lineDataSource;
};

const loadCountryBoundariesDataSources = async (Cesium: CesiumRuntime, provinceMode: ProvinceMode, provinceGeoJson?: any, selectedCountry?: string) => {
    const lineColor = selectedCountry === 'vietnam' ? getProvinceLineColor(provinceMode) : "#FFFFFF";
    
    let geojson = provinceGeoJson;
    if (selectedCountry === 'vietnam') {
        geojson = await resolveProvinceGeoJson(provinceMode, provinceGeoJson);
    }
    
    if (!geojson) return null;

    const provinceDataSource = await Cesium.GeoJsonDataSource.load(geojson, {
        clampToGround: true,
        stroke: Cesium.Color.fromCssColorString(lineColor),
        fill: Cesium.Color.fromCssColorString(PROVINCE_FILL_COLOR).withAlpha(0.08),
        strokeWidth: 1,
    });

    provinceDataSource.name = selectedCountry === 'vietnam' ? "Ranh giới tỉnh thành Việt Nam" : "Ranh giới bang/tỉnh";

    provinceDataSource.entities.values.forEach((entity) => {
        const provinceName = entity.properties?.ten_tinh?.getValue?.() || entity.properties?.shapeName?.getValue?.();
        if (provinceName) {
            entity.name = provinceName;
        }

        if (entity.polygon) {
            entity.polygon.classificationType = Cesium.ClassificationType.TERRAIN;
            entity.polygon.outline = false;
        }
    });

    return {
        polygonDataSource: provinceDataSource,
        lineDataSource: drawVietnamProvincePolylines(Cesium, geojson, lineColor),
    };
};

const CesiumGlobeView = forwardRef<CesiumGlobeViewHandle, CesiumGlobeViewProps>(({ locale, projects, provinceMode, provinceGeoJson, selectedCountry }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const viewerRef = useRef<CesiumViewer | null>(null);
    const tilesetRef = useRef<CesiumTileset | null>(null);
    const dataSourceRef = useRef<CesiumDataSource | null>(null);
    const provincePolygonDataSourceRef = useRef<CesiumDataSource | null>(null);
    const provinceLineDataSourceRef = useRef<CesiumDataSource | null>(null);
    const loadedProvinceModeRef = useRef<ProvinceMode | null>(null);
    const loadedCountryRef = useRef<string | null>(null);
    const handlerRef = useRef<CesiumScreenSpaceEventHandler | null>(null);
    const removeMoveEndRef = useRef<(() => void) | null>(null);
    const localeRef = useRef(locale);
    const projectsRef = useRef(projects);
    const provinceModeRef = useRef(provinceMode);
    const provinceGeoJsonRef = useRef(provinceGeoJson);
    const selectedCountryRef = useRef(selectedCountry);
    const [activeProject, setActiveProject] = useState<{ project: GlobeProject; x: number; y: number } | null>(null);
    const [loadError, setLoadError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isAssetReady, setIsAssetReady] = useState(false);

    const handleZoomToAsset = () => {
        const Cesium = getCesiumRuntime();
        const viewer = viewerRef.current;
        const tileset = tilesetRef.current;
        if (!Cesium || !viewer || !tileset) return;
        const sphere = tileset.boundingSphere;
        const carto = Cesium.Cartographic.fromCartesian(sphere.center);
        const lng = Cesium.Math.toDegrees(carto.longitude);
        const lat = Cesium.Math.toDegrees(carto.latitude);
        const height = Math.max(sphere.radius * 2, 1000);
        viewer.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(lng, lat, height),
            duration: 1.5,
        });
    };

    useImperativeHandle(ref, () => ({
        flyToCoords: (lat, lng, height = SINGLE_PROJECT_HEIGHT_METERS) => {
            const Cesium = getCesiumRuntime();
            const viewer = viewerRef.current;
            if (!Cesium || !viewer || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
            viewer.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(lng, lat, height),
                duration: 1.5,
            });
        },
        fitBoundsLatLng: (bounds) => {
            const Cesium = getCesiumRuntime();
            const viewer = viewerRef.current;
            if (!Cesium || !viewer) return;
            const { minLat, maxLat, minLng, maxLng } = bounds;
            if (![minLat, maxLat, minLng, maxLng].every(Number.isFinite)) return;
            if (minLat > maxLat || minLng > maxLng) return;

            const centerLat = (minLat + maxLat) / 2;
            const centerLng = (minLng + maxLng) / 2;
            const latExtentDeg = Math.max(maxLat - minLat, 0.001);
            const lngExtentDeg = Math.max(maxLng - minLng, 0.001);
            const extentMeters = Math.max(
                latExtentDeg * METERS_PER_DEGREE,
                lngExtentDeg * METERS_PER_DEGREE * Math.cos((centerLat * Math.PI) / 180),
            );
            const height = Math.min(
                Math.max(extentMeters * FIT_BOUNDS_PADDING_FACTOR, MIN_FIT_BOUNDS_HEIGHT_METERS),
                MAX_FIT_BOUNDS_HEIGHT_METERS,
            );
            viewer.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(centerLng, centerLat, height),
                duration: 1.5,
            });
        },
        resetView: () => {
            const Cesium = getCesiumRuntime();
            const viewer = viewerRef.current;
            if (!Cesium || !viewer) return;
            viewer.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(VIETNAM_CENTER.lng, VIETNAM_CENTER.lat, INITIAL_RANGE_METERS),
                duration: 1.5,
            });
        },
    }), []);

    useEffect(() => { localeRef.current = locale; }, [locale]);
    useEffect(() => { projectsRef.current = projects; }, [projects]);
    useEffect(() => { provinceModeRef.current = provinceMode; }, [provinceMode]);
    useEffect(() => { provinceGeoJsonRef.current = provinceGeoJson; }, [provinceGeoJson]);
    useEffect(() => { selectedCountryRef.current = selectedCountry; }, [selectedCountry]);

    useEffect(() => {
        let cancelled = false;

        const initViewer = async () => {
            try {
                setLoadError(false);
                setIsLoading(true);
                await loadCesiumAssets();

                const Cesium = getCesiumRuntime();
                if (cancelled || !containerRef.current || !Cesium || viewerRef.current) return;

                // Cấu hình Proxy và Access Token để tránh lộ token ở client-side
                const proxyUrl = process.env.NEXT_PUBLIC_CESIUM_SERVER_URL || (typeof window !== "undefined" ? `${window.location.origin}/api/cesium/` : "/api/cesium/");
                (Cesium as any).Ion.defaultServer = proxyUrl;
                (Cesium as any).Ion.defaultServerUrl = proxyUrl;
                Cesium.Ion.defaultAccessToken = CESIUM_TOKEN || 'proxy-managed';

                // Hook Cesium.Resource để xóa hoàn toàn tham số access_token khỏi URL của trình duyệt trước khi gửi đi
                const cesiumAny = Cesium as any;
                if (cesiumAny.Resource && !cesiumAny.Resource.prototype._isHookedForProxy) {
                    cesiumAny.Resource.prototype._isHookedForProxy = true;
                    const originalFetch = cesiumAny.Resource.prototype.fetch;
                    cesiumAny.Resource.prototype.fetch = function (options: any) {
                        try {
                            if (this.url && this.url.includes("access_token=")) {
                                const urlObj = new URL(this.url, window.location.href);
                                urlObj.searchParams.delete("access_token");
                                this.url = urlObj.toString();
                            }
                        } catch (e) {
                            console.error("Error in Cesium Resource hook:", e);
                        }
                        return originalFetch.call(this, options);
                    };
                }

                const viewerOptions: Record<string, any> = {
                    animation: false,
                    baseLayerPicker: false,
                    fullscreenButton: false,
                    geocoder: false,
                    homeButton: false,
                    infoBox: false,
                    sceneModePicker: false,
                    selectionIndicator: false,
                    timeline: false,
                    navigationHelpButton: false,
                    shouldAnimate: true,
                };

                if (!CESIUM_TOKEN) {
                    viewerOptions.baseLayer = new Cesium.ImageryLayer(
                        new Cesium.UrlTemplateImageryProvider({
                            url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
                            minimumLevel: 0,
                            maximumLevel: 19
                        })
                    );
                }

                const viewer = new Cesium.Viewer(containerRef.current, viewerOptions);
                viewerRef.current = viewer;
                viewer.scene.globe.depthTestAgainstTerrain = true;

                const initialValid = projectsRef.current
                    .map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }))
                    .filter(c => Number.isFinite(c.lat) && Number.isFinite(c.lng));

                if (initialValid.length === 1) {
                    viewer.camera.setView({
                        destination: Cesium.Cartesian3.fromDegrees(initialValid[0].lng, initialValid[0].lat, SINGLE_PROJECT_HEIGHT_METERS),
                    });
                } else {
                    viewer.camera.setView({
                        destination: Cesium.Cartesian3.fromDegrees(VIETNAM_CENTER.lng, VIETNAM_CENTER.lat, INITIAL_RANGE_METERS),
                    });
                }

                if (CESIUM_TOKEN) {
                    try {
                        const terrainProvider = await Cesium.CesiumTerrainProvider.fromIonAssetId(1);
                        if (!cancelled) {
                            viewer.terrainProvider = terrainProvider;
                        }
                    } catch (err) {
                        console.error("Failed to load Cesium World Terrain:", err);
                    }
                }

                const res = await loadCountryBoundariesDataSources(
                    Cesium,
                    provinceModeRef.current,
                    provinceGeoJsonRef.current,
                    selectedCountryRef.current
                );
                if (cancelled) return;
                if (res) {
                    const { polygonDataSource, lineDataSource } = res;
                    viewer.dataSources.add(polygonDataSource);
                    viewer.dataSources.add(lineDataSource);
                    provincePolygonDataSourceRef.current = polygonDataSource;
                    provinceLineDataSourceRef.current = lineDataSource;
                }
                loadedProvinceModeRef.current = provinceModeRef.current;
                loadedCountryRef.current = selectedCountryRef.current;

                if (CESIUM_TOKEN) {
                    try {
                        const tileset = await Cesium.Cesium3DTileset.fromIonAssetId(CESIUM_ASSET_ID);
                        if (!cancelled) {
                            viewer.scene.primitives.add(tileset);
                            tileset.show = false;
                            tilesetRef.current = tileset;
                            setIsAssetReady(true);

                            const sphere = tileset.boundingSphere;
                            const updateTilesetVisibility = () => {
                                if (!viewerRef.current || !tilesetRef.current) return;
                                const dist = Cesium.Cartesian3.distance(viewerRef.current.camera.position, sphere.center);
                                tilesetRef.current.show = dist <= sphere.radius * TILESET_SHOW_MULTIPLIER;
                            };
                            removeMoveEndRef.current = viewer.camera.moveEnd.addEventListener(updateTilesetVisibility);
                        }
                    } catch (err) {
                        console.error("Failed to load Cesium 3D Tileset:", err);
                    }
                } else {
                    setIsAssetReady(true);
                }

                const dataSource = new Cesium.CustomDataSource("projects");
                viewer.dataSources.add(dataSource);
                dataSource.clustering.enabled = true;
                dataSource.clustering.pixelRange = CLUSTER_PIXEL_RANGE;
                dataSource.clustering.minimumClusterSize = MINIMUM_CLUSTER_SIZE;
                dataSource.clustering.clusterEvent.addEventListener((entities, cluster) => {
                    cluster.label.show = false;
                    cluster.point.show = false;
                    cluster.billboard.show = true;
                    cluster.billboard.image = buildClusterBillboardImage(entities.length);
                    cluster.billboard.verticalOrigin = Cesium.VerticalOrigin.CENTER;
                    cluster.billboard.disableDepthTestDistance = Number.POSITIVE_INFINITY;
                });
                dataSourceRef.current = dataSource;

                const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
                handler.setInputAction((event: CesiumScreenSpaceClickEvent) => {
                    if (!viewerRef.current) return;
                    const touch = isTouchDevice();
                    const picked = viewerRef.current.scene.pick(event.position);

                    if (!picked) {
                        if (touch) setActiveProject(null);
                        return;
                    }

                    const project = picked.id?.properties?.project?.getValue();
                    if (project) {
                        if (touch) {
                            setActiveProject({ project, x: event.position.x, y: event.position.y });
                        } else if (project.url) {
                            window.open(getLocalizedProjectUrl(project.url, locale), "_blank", "noopener,noreferrer");
                        }
                        return;
                    }

                    if (touch) setActiveProject(null);

                    const clusterPos = picked.primitive?.position;
                    if (clusterPos) {
                        // clusterPos is ECEF; "+z" points to the north pole, not local up.
                        // Convert to geodetic so the camera rises straight above the cluster.
                        const carto = Cesium.Cartographic.fromCartesian(clusterPos);
                        const lng = Cesium.Math.toDegrees(carto.longitude);
                        const lat = Cesium.Math.toDegrees(carto.latitude);
                        const newHeight = Math.max(viewerRef.current.camera.positionCartographic.height / 4, 5000);
                        viewerRef.current.camera.flyTo({
                            destination: Cesium.Cartesian3.fromDegrees(lng, lat, newHeight),
                            duration: 1.2,
                        });
                    }
                }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

                handler.setInputAction((event: CesiumScreenSpaceMoveEvent) => {
                    if (!viewerRef.current) return;
                    if (isTouchDevice()) return;
                    const picked = viewerRef.current.scene.pick(event.endPosition);
                    const project = picked?.id?.properties?.project?.getValue();
                    if (project) {
                        setActiveProject({ project, x: event.endPosition.x, y: event.endPosition.y });
                        viewer.scene.canvas.style.cursor = "pointer";
                    } else {
                        setActiveProject(null);
                        viewer.scene.canvas.style.cursor = "";
                    }
                }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
                handlerRef.current = handler;

                viewer.camera.moveStart.addEventListener(() => {
                    if (isTouchDevice()) setActiveProject(null);
                });

                rebuildProjectEntities(Cesium, dataSource, projectsRef.current, localeRef.current);
            } catch (error) {
                console.error("Failed to initialize Cesium globe view", error);
                setLoadError(true);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        initViewer();

        return () => {
            cancelled = true;
            removeMoveEndRef.current?.();
            removeMoveEndRef.current = null;
            handlerRef.current?.destroy();
            handlerRef.current = null;
            if (viewerRef.current && dataSourceRef.current) {
                viewerRef.current.dataSources.remove(dataSourceRef.current, true);
            }
            dataSourceRef.current = null;
            if (viewerRef.current && provincePolygonDataSourceRef.current) {
                viewerRef.current.dataSources.remove(provincePolygonDataSourceRef.current, true);
            }
            if (viewerRef.current && provinceLineDataSourceRef.current) {
                viewerRef.current.dataSources.remove(provinceLineDataSourceRef.current, true);
            }
            provincePolygonDataSourceRef.current = null;
            provinceLineDataSourceRef.current = null;
            loadedProvinceModeRef.current = null;
            tilesetRef.current = null;
            if (viewerRef.current && !viewerRef.current.isDestroyed()) {
                viewerRef.current.destroy();
            }
            viewerRef.current = null;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const updateProvinceLayer = async () => {
            const Cesium = getCesiumRuntime();
            const viewer = viewerRef.current;
            if (!Cesium || !viewer) return;
            if (provinceMode === loadedProvinceModeRef.current && selectedCountry === loadedCountryRef.current) return;

            try {
                const res = await loadCountryBoundariesDataSources(Cesium, provinceMode, provinceGeoJson, selectedCountry);
                if (!res) return;
                const { polygonDataSource, lineDataSource } = res;
                if (cancelled || viewer.isDestroyed()) return;

                if (provincePolygonDataSourceRef.current) {
                    viewer.dataSources.remove(provincePolygonDataSourceRef.current, true);
                }
                if (provinceLineDataSourceRef.current) {
                    viewer.dataSources.remove(provinceLineDataSourceRef.current, true);
                }

                viewer.dataSources.add(polygonDataSource);
                viewer.dataSources.add(lineDataSource);
                provincePolygonDataSourceRef.current = polygonDataSource;
                provinceLineDataSourceRef.current = lineDataSource;
                loadedProvinceModeRef.current = provinceMode;
                loadedCountryRef.current = selectedCountry;
            } catch (error) {
                console.error("Failed to update Cesium province boundaries", error);
            }
        };

        updateProvinceLayer();

        return () => {
            cancelled = true;
        };
    }, [provinceMode, provinceGeoJson, selectedCountry]);

    useEffect(() => {
        const Cesium = getCesiumRuntime();
        const dataSource = dataSourceRef.current;
        if (!Cesium || !dataSource) return;
        rebuildProjectEntities(Cesium, dataSource, projects, locale);
        setActiveProject(null);
    }, [projects, locale]);

    const popupTitle = activeProject ? getProjectTitle(activeProject.project, locale) : "";

    return (
        <div className="absolute inset-0 z-10 bg-[#020617]">
            <style jsx global>{`
                .cesium-viewer-bottom {
                    display: none !important;
                }
            `}</style>
            <div
                ref={containerRef}
                className="absolute inset-0 [&_.cesium-credit-logoContainer]:hidden [&_.cesium-credit-textContainer]:hidden [&_.cesium-viewer-bottom]:hidden [&_.cesium-widget-credits]:hidden"
            />
            <div className="pointer-events-none absolute bottom-[2px] left-[2px] z-20 flex h-8 w-[150px] items-center justify-center rounded-full border border-gray-200 bg-white px-3 shadow-lg">
                <Image
                    src={SG3D_LOGO_SRC}
                    alt="StarGlobal 3D"
                    width={390}
                    height={69}
                    className="h-6 w-auto object-contain"
                    draggable={false}
                />
            </div>
            {isLoading && !loadError && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#020617] px-4 text-center text-sm font-semibold text-white">
                    {locale === "en" ? "Loading Cesium globe view..." : "Đang tải chế độ Cesium..."}
                </div>
            )}
            {loadError && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#020617] px-4 text-center text-white">
                    {locale === "en" ? `Could not load Cesium asset ${CESIUM_ASSET_ID}.` : `Không tải được Cesium asset ${CESIUM_ASSET_ID}.`}
                </div>
            )}
            {isAssetReady && !loadError && (
                <button
                    type="button"
                    onClick={handleZoomToAsset}
                    className="absolute right-4 top-48 md:top-[128px] z-20 rounded-full bg-[#008a00] px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-[#006d00] focus:outline-none focus:ring-2 focus:ring-white/40"
                >
                    {locale === "en" ? "Zoom to asset" : "Vị trí Bản đồ số 3D - QTSC"}
                </button>
            )}
            {activeProject && (
                <CesiumProjectPopup
                    title={popupTitle}
                    thumbnail={activeProject.project.thumbnail}
                    url={getLocalizedProjectUrl(activeProject.project.url, locale)}
                    x={activeProject.x}
                    y={activeProject.y}
                />
            )}
        </div>
    );
});

CesiumGlobeView.displayName = "CesiumGlobeView";

export default CesiumGlobeView;
